/**
 * Class Name Utility
 *
 * Utility for conditionally joining classNames together.
 * Similar to the clsx/classnames library but lightweight.
 *
 * This is used throughout the application for Tailwind CSS class merging.
 *
 * @example
 * ```tsx
 * import { cn } from '@shared/utils/cn';
 *
 * // Basic usage
 * cn('base-class', 'another-class'); // 'base-class another-class'
 *
 * // With conditionals
 * cn('base', isActive && 'active'); // 'base active' if isActive is true
 * cn('base', isActive && 'active', 'always'); // 'base active always'
 *
 * // With arrays
 * cn(['base', 'class']); // 'base class'
 *
 * // Filters out falsy values
 * cn('base', null, undefined, false, '', 'end'); // 'base end'
 * ```
 */

type ClassValue = string | number | boolean | undefined | null | ClassValue[];

/**
 * Merge CSS class names
 *
 * @param classes - Class names to merge (supports strings, booleans, arrays)
 * @returns Merged class string with falsy values filtered out
 */
export function cn(...classes: ClassValue[]): string {
  return classes
    .flat()
    .filter((x) => typeof x === 'string' && x.length > 0)
    .join(' ');
}

// Export as clsx for compatibility with components that may import it
export { cn as clsx };
